/**
 * Коллективные группы: вкладки { id: union_user_id, title } для инвестиций/архива.
 * Пустой ответ API — пустой массив (fallback вкладок в потребителях).
 */
import { useQueryWithErrorLog } from '#app/hooks/queries/useQueryWithErrorLog';
import { useMemo } from 'react';
import { Api } from 'laravel-request';
import { Text } from 'finhelper';
import { keepPreviousData } from '@tanstack/react-query';
import { QUERY_KEYS } from '#app/constants/queryKeys';
import { QUERY_CACHE_TIMES } from '#app/constants/queryCacheTimes';

interface UseUserCollectiveGroupsQueryParams {
  clientId: number | null | undefined;
  enabled?: boolean;
}

/**
 * Query hook для загрузки пользовательских коллективных групп (вкладки в архиве инвестиций).
 * Возвращает массив { id, title } для InnerTabs.
 */
export const useUserCollectiveGroupsQuery = ({
  clientId,
  enabled = true,
}: UseUserCollectiveGroupsQueryParams) => {
  const queryKey = useMemo(() => QUERY_KEYS.userCollectiveGroups.all(clientId), [clientId]);

  const query = useQueryWithErrorLog(
    {
      queryKey,
      enabled: enabled && !!clientId,
      queryFn: async () => {
        return new Promise<Array<{ id: number; title: string }>>((resolve, reject) => {
          Api.get('user-collective-group', 'index', { user_id: clientId })
            .with('union_user')
            .all(
              (response: any) => {
                const data = response?.data;
                if (!data?.length) {
                  resolve([]);
                  return;
                }
                const tabs = [
                  { id: clientId, title: 'Собственный портфель' },
                  ...data.map((row: any) => ({
                    id: row.union_user_id,
                    title: Text.fio(row.union_user),
                  })),
                ];
                resolve(tabs);
              },
              (error: any) => {
                reject(error);
              }
            );
        });
      },
      staleTime: QUERY_CACHE_TIMES.NORMAL_UPDATE,
      gcTime: QUERY_CACHE_TIMES.NORMAL_UPDATE,
      placeholderData: keepPreviousData,
    },
    'useUserCollectiveGroupsQuery'
  );

  return query;
};
